import React from 'react';
import { useTheme } from '../../ThemeContext';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import { Box, Typography, Chip, Paper, Grid } from '@mui/material';

const PurchasedNumberCard = ({ number }) => {
    const { currentTheme, themeMode } = useTheme();

    /**
     * Format phone number from +19075313116 to +1 (907) 531-3116
     */
    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return 'N/A';
        
        // Remove all non-digits
        const digits = phoneNumber.replace(/\D/g, '');
        
        // Handle different lengths
        if (digits.length === 11 && digits[0] === '1') {
            // Format as +1 (XXX) XXX-XXXX
            return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
        } else if (digits.length === 10) {
            // Assume US number without country code
            return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
        
        // For other formats, return as-is
        return phoneNumber;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        try {
            // Get user's actual browser timezone
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            
            // Handle UTC timestamps - add 'Z' if needed
            let isoStr = dateString;
            if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(dateString)) {
                isoStr = dateString + 'Z';
            }
            
            const date = new Date(isoStr);
            
            if (isNaN(date.getTime())) {
                return 'N/A';
            }
            
            const options = { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                timeZone: userTimeZone 
            };
            
            return date.toLocaleDateString('en-US', options);
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'N/A';
        }
    };

    return (
        <Paper elevation={0} sx={{
            backgroundColor: themeMode === 'dark' ? '#151138' : '#ffffff',
            border: `1px solid ${themeMode === 'dark' ? '#252144' : '#e0e0e0'}`,
            color: currentTheme.text.primary,
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            minHeight: '200px',
            maxHeight: '100%',
            boxSizing: 'border-box',
            transition: 'box-shadow 0.3s, border-color 0.3s',
            '&:hover': {
                boxShadow: themeMode === 'dark' ? '0 8px 24px rgba(0,0,0,0.3)' : '0 8px 24px rgba(0,0,0,0.1)',
                borderColor: currentTheme.common.danger,
            }
        }}>
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: currentTheme.text.secondary }}>
                        Fax Number
                    </Typography>
                    <Chip
                        label={number.status || 'Unknown'}
                        size="small"
                        sx={{
                            backgroundColor: number.status === 'active' ? 'rgba(56, 142, 60, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                            color: number.status === 'active' ? '#388E3C' : '#D32F2F',
                            textTransform: 'capitalize',
                            fontWeight: 600,
                        }}
                    />
                </Box>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                    {formatPhoneNumber(number.faxNumber)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <BusinessIcon sx={{ fontSize: '18px', color: currentTheme.text.secondary }} />
                    <Typography variant="body2" sx={{ color: currentTheme.text.secondary }}>
                        {number.isDefaultFax 
                            ? 'Assigned to all facility and departments'
                            : number.associatedTo 
                                ? (() => {
                                    const type = number.associatedTo.type?.charAt(0).toUpperCase() + number.associatedTo.type?.slice(1) || 'Unknown';
                                    const name = type === 'Department' 
                                        ? (number.departmentName || number.associatedTo.id || 'N/A')
                                        : (number.facilityName || number.associatedTo.id || 'N/A');
                                    return `${type}: ${name}`;
                                })()
                                : 'Not yet assigned'}
                    </Typography>
                </Box>
                {number.isDefaultFax && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#FFB400' }}>
                        <StarIcon sx={{ fontSize: '18px' }} />
                        <Typography variant="body2" fontWeight="500">
                            Default Number
                        </Typography>
                    </Box>
                )}
            </Box>
            <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${currentTheme.border.primary}` }}>
                <Typography variant="caption" sx={{ color: currentTheme.text.secondary }}>
                    Purchased on {formatDate(number.purchasedAt)}
                </Typography>
            </Box>
        </Paper>
    );
};

const PurchasedNumbers = ({ numbers }) => {
    const { currentTheme, themeMode } = useTheme();
    
    return (
        <Box sx={{ 
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'auto',
        }}>
            <Grid
                container 
                spacing={3}
                sx={{
                    width: '100%',
                    margin: 0,
                    '& .MuiGrid-item': {
                        paddingTop: '24px',
                        paddingLeft: '24px',
                    }
                }}
            >
                {numbers?.length > 0 ? (
                    numbers.map((number) => (
                        <Grid 
                            item 
                            xs={12}        // full width on mobile
                            sm={6}         // 2 per row
                            md={4}         // 3 per row
                            lg={3}         // 4 per row
                            key={number.numberId || number.faxNumber}
                            sx={{
                                display: 'flex',
                                width: '100%',
                                maxWidth: {
                                    xs: '100%',
                                    sm: '50%',
                                    md: '33.333%',
                                    lg: '25%',
                                }
                            }}
                        >
                            <PurchasedNumberCard number={number} />
                        </Grid>
                    ))
                ) : (
                    <Grid item xs={12}>
                        <Typography 
                            sx={{ 
                                color: themeMode === 'dark' ? '#ffffff' : '#000000',
                                textAlign: "center",
                                padding: '40px',
                            }}
                        >
                            You have not purchased any fax numbers yet.
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default PurchasedNumbers;
